import { useUser } from "@clerk/clerk-react";
import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import Layout from "./components/layout";
import Spinner from "./components/spinner";
import { useUserData } from "./hooks/useUserData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { faSlack } from "@fortawesome/free-brands-svg-icons";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isLoaded, isSignedIn } = useUser();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const teamId = searchParams.get("team");

  // Get user data to check installation status
  const { data: userData, isLoading: isUserDataLoading } = useUserData();

  // Show loading spinner while checking auth and installation status
  if (!isLoaded || isUserDataLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Spinner height="16" />
      </div>
    );
  }

  // Redirect to landing page if not signed in
  if (!isSignedIn) {
    // Include both pathname and search params in the redirect
    const fullPath = location.pathname + location.search;
    return (
      <Navigate to={`/?redirect_url=${encodeURIComponent(fullPath)}`} replace />
    );
  }

  // If no team is selected, redirect to landing page with the current URL as redirect
  if (!teamId) {
    // Include both pathname and search params in the redirect
    const fullPath = location.pathname + location.search;
    return (
      <Navigate to={`/?redirect_url=${encodeURIComponent(fullPath)}`} replace />
    );
  }

  // Check if the selected team has an inactive installation
  if (userData?.installations && userData.installations.length > 0) {
    const currentInstallation = userData.installations.find(
      (installation) => installation.team_id === teamId,
    );

    // Show reconnection message if the installation is inactive
    if (currentInstallation && !currentInstallation.is_active) {
      const handleReconnect = () => {
        window.location.href = `https://core.trybraid.io/slack/install?team=${teamId}`;
      };

      return (
        <div className="min-h-screen flex items-center justify-center bg-brandLight">
          <div className="w-full max-w-md p-8 bg-white rounded-xl shadow-md">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="text-amber-500 text-xl"
                />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                Reconnection Required
              </h1>
              <p className="text-gray-600 mb-4">
                Your connection to the{" "}
                <strong>{currentInstallation.team_name}</strong> Slack workspace
                has been disconnected.
              </p>
              <p className="text-gray-600 mb-6">
                Please reconnect to access your data and continue using Braid.
              </p>
              <button
                onClick={handleReconnect}
                className="w-full py-3 px-4 bg-brand text-white rounded-lg hover:bg-brand/90 transition-colors flex items-center justify-center"
              >
                <FontAwesomeIcon icon={faSlack} className="mr-2" />
                Reconnect to Slack
              </button>

              <div className="mt-4">
                <a
                  href="/"
                  className="text-gray-500 hover:text-gray-700 transition-colors text-sm"
                >
                  Return to Workspace Selection
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  return <Layout>{children}</Layout>;
}

export default ProtectedRoute;
